import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import styled from '@emotion/styled';
import toast from 'react-hot-toast';
import { SUBSCRIPTIONS_PLANS } from '../../../routes/routeConstants';
import { useAppSelector, useAppDisPatch } from '../../../redux/store';
import { subscriptionActions } from '../../../redux/slices/subscriptionSlice';
import useGetMySubscription from '../../../hooks/dashboard/subscription/useGetMySubscription';
import useGetEntitlements from '../../../hooks/dashboard/subscription/useGetEntitlements';
import useVerifyPayment from '../../../hooks/dashboard/subscription/useVerifyPayment';

const SubscriptionConfirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDisPatch();
  const { checkoutSession } = useAppSelector((state) => state.subscription);
  
  const { getMySubscription } = useGetMySubscription();
  const { getEntitlements } = useGetEntitlements();
  const { verifyPayment } = useVerifyPayment();
  
  const [status, setStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');
  const [retryCount, setRetryCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const attemptedReference = useRef<string | null>(null);

  const reference = searchParams.get('reference') || checkoutSession?.reference;

  useEffect(() => {
    if (attemptedReference.current === `${reference}:${retryCount}`) return;
    attemptedReference.current = `${reference}:${retryCount}`;
    let cancelled = false;
    const verify = async () => {
      if (!reference) {
        setStatus('failed');
        setErrorMessage('No payment reference was found. Return to plans and start checkout again.');
        toast.error('No payment reference found');
        return;
      }

      try {
        setErrorMessage('');
        let lastError: unknown;
        const retryDelays = [0, 1500, 3000, 5000];
        for (const delay of retryDelays) {
          if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
          if (cancelled) return;
          try {
            await verifyPayment(reference);
            lastError = undefined;
            break;
          } catch (error) {
            lastError = error;
          }
        }
        if (lastError) throw lastError;
        if (cancelled) return;
        setStatus('success');
        dispatch(subscriptionActions.setCheckoutSession(null));
        toast.success('Subscription activated!');
        await Promise.all([getMySubscription(), getEntitlements()]);
      } catch (error) {
        if (cancelled) return;
        setStatus('failed');
        const message = error instanceof Error ? error.message : 'We could not verify this payment.';
        setErrorMessage(message);
        toast.error(message);
      }
    };

    void verify();
    return () => {
      cancelled = true;
    };
  }, [reference, retryCount, verifyPayment, getMySubscription, getEntitlements, dispatch]);

  if (status === 'verifying') {
    return (
      <PageContainer>
        <StatusCard>
          <Spinner />
          <Title>Verifying Payment...</Title>
          <Subtitle>Please wait while we confirm your subscription</Subtitle>
        </StatusCard>
      </PageContainer>
    );
  }

  if (status === 'failed') {
    return (
      <PageContainer>
        <StatusCard>
          <ErrorIcon>✕</ErrorIcon>
          <StatusEyebrow>Payment status</StatusEyebrow>
          <Title>Payment Verification Failed</Title>
          <Subtitle>{errorMessage || "We couldn't verify your payment. Please try again or contact support."}</Subtitle>
          <ButtonGroup>
            {reference ? (
              <PrimaryButton onClick={() => { setStatus('verifying'); setRetryCount((count) => count + 1); }}>
                Verify payment again
              </PrimaryButton>
            ) : null}
            <SecondaryButton onClick={() => navigate(`/${SUBSCRIPTIONS_PLANS}`)}>
              Back to plans
            </SecondaryButton>
          </ButtonGroup>
        </StatusCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <StatusCard>
        <SuccessIcon>✓</SuccessIcon>
        <StatusEyebrow>Payment confirmed</StatusEyebrow>
        <Title>Subscription Activated!</Title>
        <Subtitle>Your subscription is now active. Welcome to your new plan.</Subtitle>
        <PrimaryButton onClick={() => navigate(`/${SUBSCRIPTIONS_PLANS}`)}>
          View My Plan
        </PrimaryButton>
      </StatusCard>
    </PageContainer>
  );
};

export default SubscriptionConfirmationPage;

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 500px;
  margin: 0 auto;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusCard = styled.div`
  width: 100%;
  text-align: center;
  padding: 3rem 2rem;
  border-radius: 16px;
  background: ${({ theme }) => theme.palette.white};
  border: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const StatusEyebrow = styled.p`
  margin: -0.25rem 0 0.75rem;
  color: ${({ theme }) => theme.palette.mainBlue};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${({ theme }) => theme.palette.greyGrey5};
  border-top-color: ${({ theme }) => theme.palette.mainBlue};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.1);
  color: ${({ theme }) => theme.palette.stateColorGreen};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  color: ${({ theme }) => theme.palette.stateColorRed};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const Title = styled.h1`
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.greyGrey1};
`;

const Subtitle = styled.p`
  margin: 0 0 2rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.palette.greyGrey3};
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  width: 100%;
`;

const PrimaryButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 8px;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  background: ${({ theme }) => theme.palette.mainBlue};
  color: ${({ theme }) => theme.palette.white};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.palette.mainBlue}40;
    outline-offset: 3px;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: transparent;
  color: ${({ theme }) => theme.palette.mainBlue};
  border: 1px solid ${({ theme }) => theme.palette.mainBlue};

  &:hover {
    opacity: 1;
    background: ${({ theme }) => theme.palette.greyGrey5};
  }
`;
